import {
  ArrowRightLeft,
  Box,
  Boxes,
  Camera,
  Cog,
  Database,
  DollarSign,
  Gamepad2,
  Gift,
  KeyRound,
  Languages,
  Layers,
  Package,
  Receipt,
  Settings,
  Shield,
  Ticket,
  Upload,
  Users,
} from 'lucide-react';

import { flattenGates, type DashboardItem } from '@/models/dashboardItem';
import { PagePermissions } from '@/models/pagePermissions';
import { Permissions } from '@/models/permission';

/**
 * The navigation trees, and the single source of what gates each page: the sidebar
 * renders them, the Sandbox index walks them to pick a landing page, and a Sandbox
 * switch reads them to tell whether the current URL survives. `link` is the real
 * route path under the tree's root, so those three cannot drift.
 */
export const SANDBOX_PAGES: DashboardItem[] = [
  {
    link: 'players',
    label: 'Players',
    icon: <Users size={20} />,
    items: [],
    permissions: PagePermissions.players,
  },
  {
    link: 'items',
    label: 'Catalog',
    icon: <Database size={20} />,
    permissions: PagePermissions.catalogItems,
    items: [
      {
        link: 'item',
        label: 'Items',
        icon: <Box size={16} />,
        permissions: PagePermissions.catalogItems,
      },
      {
        link: 'bundle',
        label: 'Bundles',
        icon: <Boxes size={16} />,
        permissions: PagePermissions.catalogBundles,
      },
      {
        link: 'currency',
        label: 'Currency',
        icon: <DollarSign size={16} />,
        permissions: PagePermissions.catalogCurrency,
      },
    ],
  },
  {
    link: 'season-passes',
    label: 'Season Pass',
    icon: <Ticket size={20} />,
    items: [],
    permissions: PagePermissions.seasonPasses,
  },
  {
    link: 'gifts',
    label: 'Gifts',
    icon: <Gift size={20} />,
    items: [],
    permissions: PagePermissions.gifts,
  },
  {
    link: 'translations',
    label: 'Translations',
    icon: <Languages size={20} />,
    items: [],
    permissions: PagePermissions.translations,
  },
  {
    link: 'transactions',
    label: 'Transactions',
    icon: <ArrowRightLeft size={20} />,
    items: [
      {
        link: 'store',
        label: 'Store',
        icon: <Receipt size={20} />,
        permissions: PagePermissions.transactionsStore,
      },
      {
        link: 'inventory',
        label: 'Inventory',
        icon: <Package size={20} />,
        permissions: PagePermissions.transactionsInventory,
      },
    ],
  },
  {
    link: 'store/snapshot',
    label: 'Snapshot',
    icon: <Camera size={16} />,
    permissions: PagePermissions.snapshots,
  },
  {
    link: 'settings',
    label: 'Settings',
    icon: <Cog size={20} />,
    items: [
      {
        link: 'general',
        label: 'General',
        icon: <Settings size={16} />,
        permissions: PagePermissions.settingsGeneral,
      },
      {
        link: 'import',
        label: 'Import',
        icon: <Upload size={16} />,
        permissions: PagePermissions.settingsImport,
      },
    ],
  },
];

export const GAME_PAGES: DashboardItem[] = [
  {
    link: 'sandboxes',
    label: 'Sandboxes',
    icon: <Layers size={20} />,
    items: [],
  },
  {
    link: 'access',
    label: 'Access',
    icon: <Shield size={20} />,
    items: [
      {
        link: 'roles',
        label: 'Roles',
        icon: <KeyRound size={16} />,
        // Load-bearing, and not only for this entry: gateFor treats a section
        // with any ungated child as ungated, so leaving this one out would keep
        // the whole Access section visible to everyone — and send them to a page
        // that now refuses them.
        permissions: [Permissions.roles.read],
      },
      {
        link: 'members',
        label: 'Users',
        icon: <Users size={16} />,
        permissions: [Permissions.users.managePermissions],
      },
    ],
  },
];

export const STUDIO_PAGES: DashboardItem[] = [
  {
    link: 'games',
    label: 'Games',
    icon: <Gamepad2 size={20} />,
    items: [],
  },
  {
    link: 'members',
    label: 'Members',
    icon: <Users size={20} />,
    items: [],
    // No game or sandbox in the URL here, so this checks the Studio alone — the
    // scope admin-api reads GET /v1/users on.
    permissions: [Permissions.users.read],
  },
];

/** In sidebar order, which is the preference order `firstAllowed` walks. */
export const sandboxPageGates = flattenGates(SANDBOX_PAGES);

// Paths the route tree gates more widely than the sidebar names: /store is gated as
// a whole, while the only entry pointing into it is Snapshot.
const UNLISTED_GATES = [{ to: 'store', oneOf: PagePermissions.snapshots }];

// Deepest first, so 'items/bundle' is not answered by 'items'.
const GATES_BY_DEPTH = [...sandboxPageGates, ...UNLISTED_GATES].sort(
  (a, b) => b.to.split('/').length - a.to.split('/').length,
);

/**
 * What gates the page at `pathUnderSandbox` ('gifts', 'settings/import', …), or
 * undefined when nothing does. For deciding whether a URL survives a Sandbox
 * switch: the Grants that open a page in one Sandbox may not open it in another.
 */
export const pageGateFor = (pathUnderSandbox: string) =>
  GATES_BY_DEPTH.find(
    ({ to }) =>
      pathUnderSandbox === to || pathUnderSandbox.startsWith(`${to}/`),
  )?.oneOf;
